/**
 * The game kept by the server (R-RNG-03): the screen sends each command and builds the board
 * from the steps the server answers with. The server deals the chance, so a spin's outcome is
 * known only once the server has recorded it.
 */
import { ActDto, GameDto, GameLog, UndoDto, type CreateGameBody } from '@brig/api-types';
import { otherSide, type GameSetup, type Reason } from '@brig/rules-engine';
import { fromCallback } from 'xstate';
import { ApiRequestError, type ApiClient } from '../api/client.ts';
import type { LiveFeed } from '../api/live.ts';
import { historyOf, historyReducer, type History, type HistoryError } from './history.ts';
import type { GameSource } from './screen/useGameScreen.ts';
import type { CommitInput, Committed } from './turnMachine.ts';
import type { CommitLogic } from './useTurn.ts';

export function historyFromGame(game: GameDto): History {
  return historyOf(game.setup as unknown as GameSetup, game.steps, game.nations, game.colours);
}

const plain = (message: string): HistoryError => ({ rule: null, reason: null, message });

/** Where the screen's copy of a game stands: its version, and whether a command is out. */
export interface Sync {
  version: number;
  busy: boolean;
}

/**
 * Sends commands for one game, one at a time (the turn machine waits for each answer). On a
 * version conflict — the game changed in another tab, or the other player moved first — it
 * reloads the game and says so.
 */
export function serverCommit(
  api: ApiClient,
  game: GameDto,
  sync: Sync = { version: game.version, busy: false },
): CommitLogic {
  const url = `/api/games/${game.id}`;

  async function run(input: CommitInput): Promise<History> {
    sync.busy = true;
    try {
      return await send(input);
    } finally {
      sync.busy = false;
    }
  }

  async function send({ history, command }: CommitInput): Promise<History> {
    const { version } = sync;
    try {
      switch (command.type) {
        case 'act': {
          const answer = await api.post(`${url}/actions`, { version, actions: command.actions }, ActDto);
          sync.version = answer.version;
          return historyReducer(history, { type: 'commit', steps: answer.steps });
        }
        case 'undo':
        case 'rewind': {
          const answer = await api.post(`${url}/undo`, { version, kind: command.type }, UndoDto);
          sync.version = answer.version;
          let next = history;
          for (let i = 0; i < answer.taken; i++) next = historyReducer(next, { type: command.type });
          return next;
        }
        case 'recolour': {
          const answer = await api.patch(url, { version, colours: command.colours }, GameDto);
          sync.version = answer.version;
          return historyReducer(history, { type: 'recolour', colours: answer.colours });
        }
      }
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === 'rule_violation') {
        const details = e.details as { rule: string; reason: string; message: string };
        return { ...history, error: { ...details, reason: details.reason as Reason } };
      }
      if (e instanceof ApiRequestError && e.code === 'version_conflict') {
        const fresh = await api.get(url, GameDto);
        sync.version = fresh.version;
        return { ...historyFromGame(fresh), error: plain('The game changed elsewhere and was reloaded.') };
      }
      return { ...history, error: plain(e instanceof Error ? e.message : String(e)) };
    }
  }

  return fromCallback<{ type: 'NONE' }, CommitInput>(({ input, sendBack }) => {
    let live = true;
    run(input).then(
      (history) => {
        if (live) sendBack({ type: 'COMMITTED', history } satisfies Committed);
      },
      (e: unknown) => {
        // Only the reload after a conflict can get here.
        const message = e instanceof Error ? e.message : String(e);
        if (live) sendBack({ type: 'COMMITTED', history: { ...input.history, error: plain(message) } });
      },
    );
    return () => {
      live = false;
    };
  });
}

/**
 * The other player's changes to an online game: on news of a version this screen hasn't got,
 * it reads the game again and hands it on. Not while a command of its own is out: its answer,
 * or the reload after a conflict, brings the change.
 */
export function remoteChanges(api: ApiClient, live: LiveFeed, game: GameDto, sync: Sync) {
  return (onChange: (history: History) => void) =>
    live.watch(game.id, (version) => {
      if (version <= sync.version || sync.busy) return;
      api.get(`/api/games/${game.id}`, GameDto).then(
        (fresh) => {
          if (fresh.version <= sync.version || sync.busy) return;
          sync.version = fresh.version;
          onChange(historyFromGame(fresh));
        },
        () => {
          // The next change, or the next watch after a reconnect, tries again.
        },
      );
    });
}

/** The screen's source for a server game; other games open through `onSwitch`. */
export function serverSource(
  api: ApiClient,
  live: LiveFeed,
  game: GameDto,
  onSwitch: (game: GameDto) => void,
): GameSource {
  const sync: Sync = { version: game.version, busy: false };
  const online = game.mode === 'online';
  return {
    initial: () => historyFromGame(game),
    commit: serverCommit(api, game, sync),
    devTools: game.devTools,
    ...(game.mode === 'hotseat' ? {} : { remote: otherSide(game.mySide) }),
    ...(online ? { subscribe: remoteChanges(api, live, game, sync) } : {}),
    // Another game of the same kind: against the computer again, online, or at one screen.
    newGame: async (body: CreateGameBody) => {
      onSwitch(await api.post('/api/games', { mode: game.mode, ...body }, GameDto));
    },
    importLog: async (log: unknown) => {
      onSwitch(await api.post('/api/games/import', log, GameDto));
    },
    exportLog: () => api.get(`/api/games/${game.id}/log`, GameLog),
  };
}
